import React, { useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateassetReqMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import { useUpdateMultipleDocsMutation } from '../../../../features/addassets/addassetfinalApiSlice';
import { act } from 'react';

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ApproveButton = styled(Button)`
  background-color: #2e7d32;
`;

const ReturnButton = styled(Button)`
  background-color: #ff9800;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StoreL2InboxForm = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');

  const auth = useAuth();
  let user_current_office = (useSelector((state) => state.office.off));

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  const [updateReq] = useUpdateassetReqMutation();
  const [updateMultipleAssetDocs] = useUpdateMultipleDocsMutation();
  
  

  // Check the type of request
  const isRejectionForApproval = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'lvl1req_rejectn_forappvl';
  
  // Get the store level 1 approver
  const getStoreLevel1Approver = () => {
    if (!row?.req_store || !storeoffices) return null;
    
    const storeOffice = Object.values(storeoffices).find(store => 
      store.storeoffice === row.req_store
    );
    
    return storeOffice?.roleUsers?.L1ReqApprover_A || null;
  };

  // Handler for approving request for issue
  const handleApproveRequest = async () => {
    let senttoData = {
      sent_to_tag: "lvl2req_apprvd_foraccptnce",
      action: "Request Approved and Sent to User for Acceptance",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: row?.req_store, designation: getSingleuser?.[auth?.id]?.designation[row?.req_store] },
      sent_to: { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
      time_stamp: new Date().toISOString(),
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Request approved and sent to user for acceptance");
      handleBack();
    } catch (error) {
      console.error("Error approving request:", error);
      alert("There was an error with the request. Please try again.");
    }
  };

  // Handler for returning the request to level 1 for modification
  const handleReturnToLevel1 = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for returning the request.");
      return;
    }

    const level1Id = getStoreLevel1Approver();
    
    if (!level1Id) {
      alert("Store level 1 approver not found. Please ensure the store is properly configured.");
      return;
    }

    let senttoData = {
      sent_to_tag: "lvl2req_back_formodific",
      action: "Request Returned to Level 1 Manager for Modification",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: row?.req_store, designation: "Level2Manager" },
      sent_to: { id: level1Id, name: getSingleuser?.[level1Id]?.name, office: row?.req_store, designation: "Level1Manager" },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[level1Id])}-${row?.req_store}-Level1Manager`,
      time_stamp: new Date(),
      opened: false
    };

    const assetBody = {
      assetIdArray: [
        ...(row?.bo_assets_issue?.[row.bo_assets_issue.length - 1]?.assets || []),
        ...(row?.store_assets_issue?.[row.store_assets_issue.length - 1]?.assets || [])
      ],
      updates: {
        in_transit: false,
      }
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      const res2 = await updateMultipleAssetDocs(assetBody);

      alert("Request returned to Level 1 Manager for modification");
      handleBack();
    } catch (error) {
      console.error("Error returning request:", error);
      alert("There was an error with the request. Please try again.");
    }
  };

  // Handler for approving rejection
  const handleApproveRejection = async () => {
    let senttoData = {
      sent_to_tag: "lvl2req_rejection_appvd",
      action: "Rejection Approved",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: row?.req_store, designation: "Level2Manager" },
      sent_to: { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
      curr_status: "Rejected",
      time_stamp: new Date(),
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Request rejection approved and notified to user");
      handleBack();
    } catch (error) {
      console.error("Error approving rejection:", error);
      alert("There was an error with the request. Please try again.");
    }
  };

  return (
    <>
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>

      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {isRejectionForApproval ? (
          <>
            <ApproveButton onClick={handleApproveRejection}>
              Approve Rejection
            </ApproveButton>
            <ReturnButton onClick={handleReturnToLevel1}>
              Return to Level 1 Manager
            </ReturnButton>
          </>
        ) : (
          <>
            <ApproveButton onClick={handleApproveRequest}>
              Approve Assets for Issue
            </ApproveButton>
            <ReturnButton onClick={handleReturnToLevel1}>
              Return to Level 1 Manager for Modification
            </ReturnButton>
          </>
        )}
      </div>
    </>
  );
};

export default StoreL2InboxForm; 